(() => {
  const normalize = (value) => String(value || '')
    .toLocaleLowerCase('de')
    .replace(/ä/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const stopWords = new Set([
    'aber', 'als', 'am', 'an', 'auch', 'auf', 'aus', 'bei', 'das', 'dass', 'dem',
    'den', 'der', 'des', 'die', 'ein', 'eine', 'einem', 'einen', 'einer', 'es',
    'fur', 'fuer', 'hat', 'haben', 'hier', 'ich', 'ihr', 'ihre', 'im', 'in', 'ist',
    'kann', 'mit', 'nach', 'oder', 'sich', 'sie', 'sind', 'und', 'uns', 'von', 'vor',
    'was', 'wie', 'wird', 'wo', 'zu', 'zum', 'zur'
  ]);

  const synonymGroups = [
    ['bestellstatus', 'lieferstatus', 'versandstatus'],
    ['rucksendung', 'retoure', 'retour', 'ruckgabe', 'zuruckschicken', 'reklamation'],
    ['bestellung', 'bestellungen', 'bestellen', 'bestellt', 'auftrag'],
    ['kartusche', 'kartuschen', 'patrone', 'patronen', 'tinte'],
    ['passwort', 'kennwort'],
    ['anmeldung', 'anmelden', 'login'],
    ['storung', 'fehler', 'problem']
  ];

  const synonymLookup = new Map(
    synonymGroups.flatMap((group) => group.map((word) => [word, group]))
  );

  const variantsFor = (token) => {
    const variants = new Set(synonymLookup.get(token) || [token]);
    ['ern', 'en', 'er', 'em', 'e', 'n', 's'].forEach((suffix) => {
      if (token.length - suffix.length >= 5 && token.endsWith(suffix)) {
        variants.add(token.slice(0, -suffix.length));
      }
    });
    return Array.from(variants);
  };

  const editDistance = (left, right) => {
    let previous = Array.from({ length: right.length + 1 }, (_, index) => index);
    for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
      const current = [leftIndex];
      for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
        current[rightIndex] = Math.min(
          current[rightIndex - 1] + 1,
          previous[rightIndex] + 1,
          previous[rightIndex - 1] + (left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1)
        );
      }
      previous = current;
    }
    return previous[right.length];
  };

  const createExcerpt = (text, query) => {
    const source = String(text || '').replace(/\s+/g, ' ').trim();
    if (!source) return '';
    if (source.length <= 190) return source;
    const normalizedSource = normalize(source);
    const queryTokens = normalize(query).split(' ').filter(Boolean);
    const positions = queryTokens
      .map((token) => normalizedSource.indexOf(token))
      .filter((position) => position >= 0);
    const matchPosition = positions.length ? Math.min(...positions) : 0;
    let start = matchPosition > 90 ? Math.max(0, source.lastIndexOf(' ', matchPosition - 60)) : 0;
    let end = Math.min(source.length, start + 190);
    if (end < source.length) end = source.lastIndexOf(' ', end);
    return `${start > 0 ? '… ' : ''}${source.slice(start, end).trim()}${end < source.length ? ' …' : ''}`;
  };

  const prepareSearch = (root) => {
    const form = root.querySelector('[data-faq-search-form]');
    const input = root.querySelector('[data-faq-search-input]');
    const clearButton = root.querySelector('[data-faq-search-clear]');
    const backButton = root.querySelector('[data-faq-search-back]');
    const validation = root.querySelector('[data-faq-search-validation]');
    const results = root.querySelector('[data-faq-search-results]');
    const heading = root.querySelector('[data-faq-results-heading]');
    const description = root.querySelector('[data-faq-results-description]');
    const resultList = root.querySelector('[data-faq-results-list]');
    const indexNode = root.querySelector('[data-faq-search-index]');
    const defaultNodes = Array.from(root.querySelectorAll('[data-faq-default]'));

    if (!form || !input || !results || !resultList || !indexNode) return;

    let entries = [];
    try {
      entries = JSON.parse(indexNode.textContent || '[]');
    } catch (error) {
      entries = [];
    }

    const searchable = entries.map((entry, order) => {
      const fields = [
        normalize(entry.question),
        normalize(entry.keywords),
        normalize(`${entry.category_title} ${entry.subcategory_title}`),
        normalize(entry.answer_text)
      ];
      return {
        ...entry,
        order,
        fields,
        tokenFields: fields.map((field) => field.split(' ').filter(Boolean))
      };
    });

    const suggestionTerms = new Map();
    searchable.forEach((entry) => {
      entry.tokenFields.slice(0, 3).flat().forEach((term) => {
        if (term.length >= 6 && !stopWords.has(term) && !suggestionTerms.has(term)) {
          suggestionTerms.set(term, term.charAt(0).toLocaleUpperCase('de') + term.slice(1));
        }
      });
    });

    const updateUrl = (query) => {
      const url = new URL(window.location.href);
      if (query) url.searchParams.set('q', query);
      else url.searchParams.delete('q');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    };

    const setDefaultVisibility = (visible) => {
      defaultNodes.forEach((node) => {
        node.hidden = !visible;
      });
    };

    const reset = (focus = true) => {
      input.value = '';
      clearButton.hidden = true;
      backButton.hidden = true;
      validation.textContent = '';
      results.hidden = true;
      resultList.replaceChildren();
      setDefaultVisibility(true);
      root.classList.remove('is-searching');
      updateUrl('');
      if (focus) input.focus({ preventScroll: true });
    };

    const findSuggestion = (query) => {
      const normalizedQuery = normalize(query);
      if (normalizedQuery.length < 7 || normalizedQuery.includes(' ')) return '';
      const limit = normalizedQuery.length >= 10 ? 2 : 1;
      let best = '';
      let bestDistance = limit + 1;
      suggestionTerms.forEach((display, term) => {
        if (term === normalizedQuery || Math.abs(term.length - normalizedQuery.length) > limit) return;
        const distance = editDistance(normalizedQuery, term);
        if (distance < bestDistance) {
          best = display;
          bestDistance = distance;
        }
      });
      return bestDistance <= limit ? best : '';
    };

    const render = (query, matches) => {
      setDefaultVisibility(false);
      root.classList.add('is-searching');
      results.hidden = false;

      if (!matches.length) {
        heading.textContent = `${root.dataset.faqNoResults} „${query}“`;
        description.textContent = root.dataset.faqNoResultsHelp;
        const suggestion = findSuggestion(query);
        resultList.innerHTML = suggestion
          ? `<p class="faq-search-suggestion">${escapeHtml(root.dataset.faqSuggestion || 'Meinten Sie')} <button type="button" data-faq-suggestion>${escapeHtml(suggestion)}</button>?</p>`
          : '';
        const suggestionButton = resultList.querySelector('[data-faq-suggestion]');
        if (suggestionButton) {
          suggestionButton.addEventListener('click', () => {
            input.value = suggestion;
            runSearch();
            input.focus({ preventScroll: true });
          });
        }
        return;
      }

      heading.textContent = `${root.dataset.faqResultsFor} „${query}“`;
      description.textContent = matches.length === 1
        ? root.dataset.faqResultsCountOne
        : root.dataset.faqResultsCountOther.replace('[count]', String(matches.length));
      resultList.innerHTML = matches.slice(0, 12).map(({ entry }) => `
        <a class="faq-search-result" href="${escapeHtml(entry.url)}">
          <h3>${escapeHtml(entry.question)}</h3>
          <p class="faq-search-result__meta">
            <span>${escapeHtml(entry.category_title)}</span>
            <span class="faq-search-result__separator" aria-hidden="true">›</span>
            <span>${escapeHtml(entry.subcategory_title)}</span>
          </p>
          <p class="faq-search-result__excerpt">${escapeHtml(createExcerpt(entry.answer_text, query))}</p>
        </a>
      `).join('');
    };

    const runSearch = (submitted = false) => {
      const query = input.value.trim();
      validation.textContent = '';
      updateUrl(query);

      if (!query) {
        reset(false);
        if (submitted) {
          validation.textContent = root.dataset.faqValidation;
          input.focus();
        }
        return;
      }

      clearButton.hidden = false;
      backButton.hidden = false;
      const tokens = normalize(query)
        .split(' ')
        .filter((token) => token.length > 1 && !stopWords.has(token));
      const effectiveTokens = tokens.length ? tokens : normalize(query).split(' ').filter((token) => token.length > 1);
      const normalizedPhrase = normalize(query);

      const matches = searchable
        .map((entry) => {
          let score = 0;
          for (const token of effectiveTokens) {
            const variants = variantsFor(token);
            if (token === 'bestellstatus') {
              const titleWords = entry.tokenFields[0];
              const hasStatusIntent = titleWords.some((word) => (
                ['bestellstatus', 'lieferstatus', 'versandstatus'].includes(word)
              )) || (
                titleWords.includes('status') &&
                titleWords.some((word) => ['bestellung', 'lieferung', 'versand'].includes(word))
              );
              if (!hasStatusIntent) return null;
              score += 200;
            }
            let tokenScore = 0;
            variants.forEach((variant) => {
              entry.tokenFields.forEach((words, fieldIndex) => {
                const matched = words.some((word) => (
                  word === variant ||
                  (variant.length >= 5 && word.startsWith(variant)) ||
                  (word.length >= 5 && variant.startsWith(word))
                ));
                if (matched) tokenScore = Math.max(tokenScore, [100, 72, 42, 12][fieldIndex]);
              });
            });
            if (!tokenScore) return null;
            score += tokenScore;
          }
          if (normalizedPhrase && entry.fields[0].includes(normalizedPhrase)) score += 35;
          return { entry, score };
        })
        .filter(Boolean)
        .sort((left, right) => right.score - left.score || left.entry.order - right.entry.order);

      render(query, matches);
    };

    let timer;
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      window.clearTimeout(timer);
      runSearch(true);
      if (!results.hidden) heading.focus({ preventScroll: true });
    });
    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      clearButton.hidden = !input.value;
      timer = window.setTimeout(() => runSearch(), 180);
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        reset();
      }
    });
    clearButton.addEventListener('click', () => reset());
    backButton.addEventListener('click', () => {
      const scrollPosition = window.scrollY;
      reset(false);
      input.focus({ preventScroll: true });
      window.scrollTo({ top: scrollPosition, behavior: 'auto' });
    });

    const initialQuery = new URL(window.location.href).searchParams.get('q');
    if (initialQuery) {
      input.value = initialQuery;
      runSearch();
    }
  };

  const prepareAccordion = (root) => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const requestedHandle = new URL(window.location.href).searchParams.get('faq');
    let requestedItem;

    root.querySelectorAll('[data-faq-item]').forEach((item) => {
      const summary = item.querySelector('summary');
      const answer = item.querySelector('[data-faq-answer]');
      if (!summary || !answer) return;

      let expanded = item.open;
      let animating = false;
      let fallbackTimer;

      const finish = (open) => {
        window.clearTimeout(fallbackTimer);
        item.open = open;
        expanded = open;
        animating = false;
        answer.style.height = open ? 'auto' : '0px';
        answer.style.opacity = open ? '1' : '0';
      };

      const setOpen = (open) => {
        if (animating || expanded === open) return;
        if (prefersReducedMotion) {
          finish(open);
          return;
        }

        animating = true;
        answer.style.transition = 'height 220ms ease, opacity 180ms ease';

        if (open) {
          item.open = true;
          answer.style.height = '0px';
          answer.style.opacity = '0';
          answer.getBoundingClientRect();
          answer.style.height = `${answer.scrollHeight}px`;
          answer.style.opacity = '1';
        } else {
          answer.style.height = `${answer.scrollHeight}px`;
          answer.style.opacity = '1';
          answer.getBoundingClientRect();
          answer.style.height = '0px';
          answer.style.opacity = '0';
        }

        const onEnd = (event) => {
          if (event.propertyName !== 'height') return;
          answer.removeEventListener('transitionend', onEnd);
          finish(open);
        };
        answer.addEventListener('transitionend', onEnd);
        fallbackTimer = window.setTimeout(() => {
          answer.removeEventListener('transitionend', onEnd);
          finish(open);
        }, 320);
      };

      answer.style.height = item.open ? 'auto' : '0px';
      answer.style.opacity = item.open ? '1' : '0';
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        setOpen(!expanded);
      });

      if (requestedHandle && item.dataset.faqItemHandle === requestedHandle) {
        item.open = true;
        expanded = true;
        answer.style.height = 'auto';
        answer.style.opacity = '1';
        requestedItem = item;
      }
    });

    if (requestedItem) {
      window.requestAnimationFrame(() => {
        window.setTimeout(() => requestedItem.scrollIntoView({ block: 'start' }), 80);
      });
    }
  };

  const prepareAnswerTools = (root) => {
    root.querySelectorAll('[data-faq-item]').forEach((item) => {
      const status = item.querySelector('[data-faq-item-status]');
      const storageKey = `fp-faq-vote:${item.dataset.faqItemHandle}`;

      item.querySelectorAll('[data-faq-vote]').forEach((button) => {
        try {
          button.setAttribute(
            'aria-pressed',
            String(window.localStorage.getItem(storageKey) === button.dataset.faqVote)
          );
        } catch (error) {
          button.setAttribute('aria-pressed', 'false');
        }

        button.addEventListener('click', () => {
          item.querySelectorAll('[data-faq-vote]').forEach((candidate) => {
            candidate.setAttribute('aria-pressed', String(candidate === button));
          });
          try {
            window.localStorage.setItem(storageKey, button.dataset.faqVote);
          } catch (error) {
            status.textContent = root.dataset.faqVoteSaved;
          }
          status.textContent = root.dataset.faqVoteSaved;
        });
      });

      const copyButton = item.querySelector('[data-faq-copy]');
      if (copyButton) {
        copyButton.addEventListener('click', async () => {
          const url = new URL(window.location.href);
          url.searchParams.delete('q');
          url.searchParams.set('faq', item.dataset.faqItemHandle);
          url.hash = item.dataset.faqSubcategory;
          try {
            await navigator.clipboard.writeText(url.toString());
            status.textContent = root.dataset.faqCopied;
          } catch (error) {
            status.textContent = url.toString();
          }
        });
      }
    });
  };

  document.querySelectorAll('[data-faq-root]').forEach((root) => {
    prepareSearch(root);
    prepareAccordion(root);
    prepareAnswerTools(root);
  });
})();
